/**
 * SoleadLab 2.0 - Map Data Point Detail Popup
 *
 * Rich popup shown when a data point dot is clicked on the map.
 * Displays the same info as the SLD PointDetailPopup: metrics table,
 * alarm state, triggered rules, display metric selector, and action buttons.
 */

import { useState } from 'react';
import { useMonitoringStore } from '../../stores';
import { useUIStore } from '../../stores';
import type {
  AlarmState,
  TriggeredAdvancedRule,
} from '@/types/monitoring';
import './MapPointDetailPopup.css';

// ============================================
// Helpers
// ============================================

function stateClass(state: string): string {
  return `dp-popup-state--${state}`;
}

function stateTextClass(state: string): string {
  return `dp-popup-state-text--${state}`;
}

function formatMetricValue(v: number | string | null, decimals = 2): string {
  if (v == null) return '—';
  if (typeof v === 'string') return v;
  return v.toFixed(decimals);
}

function opLabel(op: string): string {
  switch (op) {
    case 'lt': return '<';
    case 'lte': return '≤';
    case 'gt': return '>';
    case 'gte': return '≥';
    case 'eq': return '=';
    case 'neq': return '≠';
    default: return op;
  }
}

// ============================================
// Types (locally derived from monitoring store; avoids new external imports)
// ============================================

type MonitoringStoreState = ReturnType<typeof useMonitoringStore.getState>;
type ProjectConfigType = NonNullable<MonitoringStoreState['projectConfig']>;
type DPConfig = ProjectConfigType['datapoints'][number];
type RuntimeState = NonNullable<ReturnType<MonitoringStoreState['runtimeStates']['get']>>;

// ============================================
// Props
// ============================================

interface MapPointDetailPopupProps {
  dpId: string;
  position: { x: number; y: number };
  onClose: () => void;
}

// ============================================
// Component
// ============================================

export default function MapPointDetailPopup({
  dpId,
  position,
  onClose,
}: MapPointDetailPopupProps) {
  const projectConfig = useMonitoringStore((s) => s.projectConfig);
  const runtimeStates = useMonitoringStore((s) => s.runtimeStates);
  const triggeredAdvancedRules = useMonitoringStore((s) => s.triggeredAdvancedRules);
  const sldDiagrams = useMonitoringStore((s) => s.sldDiagrams);
  const updateDataPointConfig = useMonitoringStore((s) => s.updateDataPointConfig);

  const config = projectConfig?.datapoints.find((d) => d.id === dpId);
  const runtime = runtimeStates.get(dpId);
  if (!config) return null;

  // Render the hook-using body only after `config` is proven non-null. Mounting
  // the child here (rather than placing useState above the early return) is what
  // preserves the original initialization semantics for `displayMetric`: state
  // is initialized when the non-null detail UI first renders, not when the
  // outer component first mounts with config still unresolved.
  return (
    <MapPointDetailBody
      config={config}
      runtime={runtime}
      dpId={dpId}
      position={position}
      onClose={onClose}
      advancedRules={projectConfig?.advanced_rules ?? []}
      triggeredAdvancedRules={triggeredAdvancedRules}
      sldDiagrams={sldDiagrams}
      updateDataPointConfig={updateDataPointConfig}
    />
  );
}

// ============================================
// Body (renders only when config is non-null)
// ============================================

interface MapPointDetailBodyProps {
  config: DPConfig;
  runtime: RuntimeState | undefined;
  dpId: string;
  position: { x: number; y: number };
  onClose: () => void;
  advancedRules: ProjectConfigType['advanced_rules'];
  triggeredAdvancedRules: MonitoringStoreState['triggeredAdvancedRules'];
  sldDiagrams: MonitoringStoreState['sldDiagrams'];
  updateDataPointConfig: MonitoringStoreState['updateDataPointConfig'];
}

function MapPointDetailBody({
  config,
  runtime,
  dpId,
  position,
  onClose,
  advancedRules,
  triggeredAdvancedRules,
  sldDiagrams,
  updateDataPointConfig,
}: MapPointDetailBodyProps) {
  // Hook called unconditionally at the top of the body. `config` is guaranteed
  // non-null by the parent guard, so the initializer is safe.
  const [displayMetric, setDisplayMetric] = useState(config.ui?.display_metric ?? '');

  const state: AlarmState = runtime?.state ?? 'no_data';
  const values = runtime?.values ?? {};
  const metrics = Object.entries(values);
  const availableMetrics = config.selected_metrics ?? [];

  // Data-point-level triggered rules
  const triggeredRules = runtime?.triggered_rules ?? [];
  const triggeredGroups = runtime?.triggered_groups ?? [];

  // Advanced rules triggered and referencing this data point's entity
  const entityId = config.entity_id;
  const relevantTriggeredAdvanced: Array<{
    rule: typeof advancedRules[number];
    triggered: TriggeredAdvancedRule;
  }> = [];

  for (const triggered of triggeredAdvancedRules) {
    const rule = advancedRules.find((r) => r.id === triggered.rule_id);
    if (!rule) continue;
    const referencesEntity = triggered.matched_metrics?.some(
      (m) => m.entity_id === entityId,
    );
    if (referencesEntity) {
      relevantTriggeredAdvanced.push({ rule, triggered });
    }
  }

  const hasTriggeredRules =
    triggeredRules.length > 0 ||
    triggeredGroups.length > 0 ||
    relevantTriggeredAdvanced.length > 0;

  // Find SLD diagram containing this data point
  const sldWithPoint = sldDiagrams.find((d) =>
    d.dataPointMappings?.some((m) => m.dataPointId === dpId),
  );

  // Position clamped to viewport
  const popupStyle: React.CSSProperties = {
    left: Math.min(Math.max(position.x + 10, 16), window.innerWidth - 340),
    top: Math.min(Math.max(position.y - 100, 16), window.innerHeight - 400),
  };

  function handleSaveDisplayMetric() {
    updateDataPointConfig(dpId, {
      ui: { ...config.ui, display_metric: displayMetric || undefined },
    });
    onClose();
  }

  function handleEdit() {
    onClose();
    useUIStore.getState().openModal('editDataPoint', { datapointId: dpId });
  }

  function handleRemove() {
    updateDataPointConfig(dpId, { coordinates: undefined });
    onClose();
  }

  function handleOpenSLD() {
    if (!sldWithPoint) return;
    onClose();
    useUIStore.getState().openModal('sldViewer', { sldId: sldWithPoint.id, focusPointId: dpId });
  }

  return (
    <>
      <div className="dp-popup-backdrop" onClick={onClose} />
      <div className="dp-popup" style={popupStyle}>
        {/* Header */}
        <div className="dp-popup__header">
          <span className={`dp-popup__dot ${stateClass(state)}`} />
          <span className="dp-popup__name">{config.display_name}</span>
          <span className={`dp-popup__state ${stateTextClass(state)}`}>
            {state.toUpperCase().replace('_', ' ')}
          </span>
        </div>

        {/* Entity ID */}
        <div className="dp-popup__entity">Entity: {config.entity_id}</div>

        {/* Metrics table */}
        {metrics.length > 0 && (
          <div className="dp-popup__metrics">
            {metrics.map(([key, val]) => (
              <div key={key} className="dp-popup__metric-row">
                <span className="dp-popup__metric-key">{key}</span>
                <span className="dp-popup__metric-val">
                  {formatMetricValue(val, config.decimals)}
                  {(config.metric_units?.[key] ?? config.unit) ? ` ${config.metric_units?.[key] ?? config.unit}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Triggered rules */}
        {hasTriggeredRules && (
          <div className="dp-popup__rules">
            <div className="dp-popup__rules-title">Triggered Rules</div>

            {triggeredRules.map((tr) => (
              <div
                key={tr.rule_id}
                className={`dp-popup__rule dp-popup__rule--${tr.severity}`}
              >
                <span className="dp-popup__rule-badge">
                  {tr.severity.toUpperCase()}
                </span>
                <span className="dp-popup__rule-detail">
                  {tr.metric} {opLabel(tr.op)} {tr.value}
                  {tr.actual != null && (
                    <span className="dp-popup__rule-actual">
                      {' '}(actual: {formatMetricValue(tr.actual, config.decimals)})
                    </span>
                  )}
                </span>
                {tr.message && (
                  <div className="dp-popup__rule-msg">{tr.message}</div>
                )}
              </div>
            ))}

            {triggeredGroups.map((tg) => (
              <div
                key={tg.group_id}
                className={`dp-popup__rule dp-popup__rule--${tg.severity}`}
              >
                <span className="dp-popup__rule-badge">
                  {tg.severity.toUpperCase()}
                </span>
                <span className="dp-popup__rule-detail">
                  {tg.group_name || 'Rule Group'} ({tg.combinator})
                </span>
                {tg.message && (
                  <div className="dp-popup__rule-msg">{tg.message}</div>
                )}
                <div className="dp-popup__rule-conditions">
                  {tg.triggered_conditions.map((c, i) => (
                    <div key={i} className="dp-popup__rule-cond">
                      {c.metric} {opLabel(c.op)} {c.value}
                      {c.actual != null && (
                        <span className="dp-popup__rule-actual">
                          {' '}(actual: {formatMetricValue(c.actual, config.decimals)})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {relevantTriggeredAdvanced.map(({ rule, triggered }) => (
              <div
                key={rule.id}
                className={`dp-popup__rule dp-popup__rule--${triggered.severity}`}
              >
                <span className="dp-popup__rule-badge">
                  {triggered.severity.toUpperCase()}
                </span>
                <span className="dp-popup__rule-detail">
                  {triggered.rule_name}
                  <span className="dp-popup__rule-type">
                    {rule.type.replace('_', ' ')}
                  </span>
                </span>
                {triggered.details && (
                  <div className="dp-popup__rule-msg">{triggered.details}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Display Metric on Map */}
        {availableMetrics.length > 0 && (
          <div className="dp-popup__display-metric">
            <label>Display Metric on Map</label>
            <select
              value={displayMetric}
              onChange={(e) => setDisplayMetric(e.target.value)}
            >
              <option value="">None (label only)</option>
              <option value="__all__">All selected metrics</option>
              {availableMetrics.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        )}

        {/* Actions */}
        <div className="dp-popup__actions">
          {availableMetrics.length > 0 && (
            <button className="dp-popup__btn dp-popup__btn--primary" onClick={handleSaveDisplayMetric}>
              Save
            </button>
          )}
          <button className="dp-popup__btn" onClick={handleEdit}>
            Edit
          </button>
          {sldWithPoint && (
            <button className="dp-popup__btn" onClick={handleOpenSLD}>
              SLD
            </button>
          )}
          <button className="dp-popup__btn dp-popup__btn--danger" onClick={handleRemove}>
            Remove
          </button>
        </div>
      </div>
    </>
  );
}
